import { type FormEvent, useCallback, useRef, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { Markdown } from "../components/markdown"
import { CommentEditor } from "../components/CommentEditor"
import { CommentDisplay } from "../components/CommentDisplay"
import { MarkdownField, type MarkdownFieldHandle } from "../components/MarkdownField"
import { differences } from "../lib/markdown-diff"
import {
  ChevronDown,
  Clock,
  LevelIcon,
  Pencil,
  Plus,
  PriorityIcon,
  StatusIcon,
  TypeIcon,
} from "../components/icons"
import { TaskCreateModal } from "../components/TaskCreateModal"
import { EntryDialog } from "../components/timesheet"
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  Empty,
  ErrorNote,
  InlineEdit,
  type MenuItem,
  Modal,
  Progress,
  RelativeTime,
  Skeleton,
  describeSpentOfEstimate,
  formatDate,
  formatDuration,
  formatSpentOfEstimate,
  fromDateInput,
  isOverdue,
  parseDuration,
  toDateInput,
  useLoad,
  useToast,
} from "../components/ui"
import { useRoster } from "./work"
import { assigneeLabel, assigneePickerItems } from "../lib/assignees"
import {
  ApiError,
  type Comment,
  type EditableTimeEntry,
  type Project,
  type Sprint,
  type Task,
  type TaskLevel,
  type TimeEntry,
  type TimeLog,
  api,
} from "../lib/api"
import { useSession } from "../lib/auth"
import { editableEntry } from "../lib/timesheet"
import { attachmentRef } from "../lib/attachment-refs"
import { type CommentSort, readCommentSort, saveCommentSort, sortComments } from "../lib/commentSort"
import { BoardConfigProvider, DEFAULT_CONFIG, prioritiesOf, priorityOf, statusOf, typeOf } from "../lib/boardConfig"
import { CHILDREN_LABEL, LEVEL_LABELS, allowedLevels, childLevelOf, parentLevelOf } from "../lib/levels"

// One task: what it is and what was said about it on the left, what it *is* on
// the right.
//
// The split is the point. Properties are glanceable and never scroll away from
// the reader's mental model of the task, while the description, the children and
// the thread — the things a reviewer actually reads — get the full column width.
// An epic, a task and a sub-task all use this page; the level decides what the
// children are called and which parent it may have.

export function TaskDetailPage() {
  const { taskId = "" } = useParams()
  const { roster, assignable } = useRoster()
  const toast = useToast()
  const [creatingChild, setCreatingChild] = useState(false)
  const [commentSort, setCommentSort] = useState<CommentSort>(readCommentSort)
  const task = useLoad(
    () =>
      api.get<{ task: Task; descriptionAttachments?: { id: string; url: string }[] }>(
        `/projects/tasks/${taskId}`,
      ),
    [taskId],
  )
  // The board's own vocabulary. Loaded after the task because the project id
  // comes from it; everything below falls back to defaults until it lands.
  const projectId = task.data?.task.projectId
  const projectLoad = useLoad(
    () => (projectId ? api.get<{ project: Project }>(`/projects/${projectId}`) : Promise.resolve(null)),
    [projectId],
  )
  const config = projectLoad.data?.project.config ?? DEFAULT_CONFIG

  // The description's images: refs resolved at paint against this list, and
  // uploads from the editor claimed by the next description PATCH.
  const descriptionAttachments = task.data?.descriptionAttachments ?? []
  const resolveDescriptionHref = useCallback(
    (url: string) => descriptionAttachments.find((a) => `attachment:${a.id}` === url)?.url ?? null,
    [descriptionAttachments],
  )
  const [, setDescriptionAttachmentIds] = useState<string[]>([])
  const uploadDescriptionInline = useCallback(async (file: File) => {
    const form = new FormData()
    form.append("file", file)
    const draft = await api.postMultipart<{ id: string; url: string }>("/projects/attachments", form)
    setDescriptionAttachmentIds((current) => [...current, draft.id])
    return attachmentRef(draft.id)
  }, [])

  const patch = async (body: Record<string, unknown>, message?: string) => {
    try {
      await api.patch(`/projects/tasks/${taskId}`, body)
      if (message) toast(message)
      task.reload()
    } catch (error) {
      toast(error instanceof Error ? error.message : "Could not save that", "error")
    }
  }

  if (task.loading) return <Skeleton className="h-96" />
  if (task.error) return <ErrorNote error={task.error} />
  const t = task.data?.task
  if (!t) return <Empty title="Task not found" />

  // By category rather than the literal "done": a child's status is one of
  // the board's own, and "Shipped" is as finished as "Done".
  const liveChildren = t.children?.filter((c) => !c.archivedAt) ?? []
  const childTotal = liveChildren.length
  const childDone = liveChildren.filter((c) => statusOf(config, c.status).category === "done").length

  return (
    // The icons read the board from context, so a custom status or priority
    // renders with its own name and colour here as on the board.
    <BoardConfigProvider value={config}>
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
      <div className="flex min-w-0 flex-1 flex-col gap-6">
        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-xs text-faint">
            <Link to={`/projects/${t.projectId}`} className="hover:text-muted hover:underline">
              Project
            </Link>
            <span>/</span>
            {/* Project / EPIC / TASK / SUB: a sub-task names its epic too. */}
            {[t.epic, t.parent].map(
              (ancestor) =>
                ancestor && (
                  <span key={ancestor.id} className="contents">
                    <Link
                      to={`/tasks/${ancestor.id}`}
                      title={`${LEVEL_LABELS[ancestor.level]}: ${ancestor.title}`}
                      className="font-mono hover:text-muted hover:underline"
                    >
                      {ancestor.key}
                    </Link>
                    <span>/</span>
                  </span>
                ),
            )}
            <LevelIcon level={t.level} />
            <span className="font-mono">{t.key}</span>
            {t.archivedAt && <Badge tone="warning">Archived</Badge>}
          </div>

          <InlineEdit
            ariaLabel="Task title"
            value={t.title}
            className="text-2xl font-semibold tracking-tight text-foreground"
            inputClassName="text-2xl font-semibold tracking-tight text-foreground"
            onSave={(title) => (title ? patch({ title }, "Title saved") : undefined)}
          />
        </header>

        <Section title="Description">
          <EditableMarkdown
            value={t.description}
            ariaLabel="Task description"
            placeholder="No description. The agent usually writes this."
            onSave={(description) => {
              // Description edits that add images claim them by ref; the PATCH
              // carries the ids, mirroring the create form. Only new ids are
              // sent — already-claimed ones the server leaves alone.
              const refs = Array.from(
                description.matchAll(/attachment:([0-9a-f-]{36})/g),
                (m) => m[1]!,
              ).filter((id) => !descriptionAttachments.some((a) => a.id === id))
              setDescriptionAttachmentIds(refs)
              patch(
                refs.length ? { description, attachmentIds: refs } : { description },
                "Description saved",
              )
            }}
            resolveHref={resolveDescriptionHref}
            onUpload={uploadDescriptionInline}
          />
        </Section>

        {(t.filesToModify?.length || t.implementationNotes) && (
          <Section title="For the change">
            <div className="flex flex-col gap-3">
              {/* The paths the agent claims it read, as it reached them. Shown
                  without being asked for, because they are what makes a
                  generated sub-task checkable — and when work spans two
                  systems, the leading folder says which one this is in. */}
              {!!t.filesToModify?.length && (
                <ul className="flex flex-wrap gap-1">
                  {t.filesToModify.map((file) => (
                    <li
                      key={file}
                      className="rounded border border-border bg-surface-muted px-1.5 py-0.5 font-mono text-[11px] text-muted"
                    >
                      {file}
                    </li>
                  ))}
                </ul>
              )}
              {t.implementationNotes && (
                <div className="rounded-md bg-surface-muted px-3 py-2">
                  <p className="mb-1 text-[10px] tracking-wide text-faint uppercase">Implementation notes</p>
                  <Markdown>{t.implementationNotes}</Markdown>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* An epic holds tasks and a task holds sub-tasks; a sub-task holds
            nothing, so it gets no section to invite an error. */}
        {childLevelOf(t.level) && (
          <Section
            title={t.level === "epic" ? "Tasks" : "Sub-tasks"}
            aside={
              <div className="flex items-center gap-3">
                {childTotal > 0 && (
                  <Progress done={childDone} total={childTotal} label={CHILDREN_LABEL[t.level]} />
                )}
                <Button variant="subtle" size="sm" onClick={() => setCreatingChild(true)}>
                  <Plus /> {t.level === "epic" ? "New task" : "New sub-task"}
                </Button>
              </div>
            }
          >
            {t.children?.length ? (
              <ul className="flex flex-col rounded-lg border border-border">
                {t.children.map((child) => (
                  <ChildRow key={child.id} task={child} config={config} />
                ))}
              </ul>
            ) : (
              <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-faint">
                {t.level === "epic"
                  ? "No tasks yet. Add one with the button above, join it from the task's own page under Parent, or let the agent file it here when it plans the work."
                  : "No sub-tasks yet. Add one with the button above, move one here from its own page, or let the agent break the task down."}
              </p>
            )}
          </Section>
        )}

        <Section
          title="Discussion"
          aside={
            <Button
              variant="subtle"
              size="sm"
              title="Change comment order"
              onClick={() => {
                const next = commentSort === "newest" ? "oldest" : "newest"
                setCommentSort(next)
                saveCommentSort(next)
              }}
            >
              <ChevronDown className={commentSort === "oldest" ? "size-4 rotate-180" : "size-4"} />
              {commentSort === "newest" ? "Newest first" : "Oldest first"}
            </Button>
          }
        >
          <CommentThread taskId={taskId} sort={commentSort} />
        </Section>
      </div>

      <aside className="flex w-full shrink-0 flex-col gap-1 lg:w-72">
        <Property label="Status">
          <PickerButton
            current={
              <>
                <StatusIcon status={t.status} /> {statusOf(config, t.status).name}
              </>
            }
            items={config.statuses.map((status) => ({
              label: (
                <span className="flex items-center gap-2">
                  <StatusIcon status={status.id} /> {status.name}
                </span>
              ),
              selected: status.id === t.status,
              onSelect: () => patch({ status: status.id }),
            }))}
          />
        </Property>

        <Property label="Priority">
          <PickerButton
            current={
              <>
                <PriorityIcon priority={t.priority} /> {priorityOf(config, t.priority).name}
              </>
            }
            items={prioritiesOf(config).map((priority) => ({
              label: (
                <span className="flex items-center gap-2">
                  <PriorityIcon priority={priority.id} /> {priority.name}
                </span>
              ),
              selected: priority.id === t.priority,
              onSelect: () => patch({ priority: priority.id }),
            }))}
          />
        </Property>

        <Property label="Assignee">
          <PickerButton
            current={
              <>
                <Avatar name={t.assigneeName} id={t.assigneeId} size="xs" />
                {assigneeLabel(t)}
              </>
            }
            items={assigneePickerItems(assignable, t).map((item) => ({
              label:
                item.id === null ? (
                  item.label
                ) : (
                  <span className="flex items-center gap-2">
                    <Avatar name={item.name} id={item.id} size="xs" /> {item.label}
                  </span>
                ),
              selected: item.selected,
              disabled: item.disabled,
              onSelect: () => patch({ assigneeId: item.id }),
            }))}
          />
        </Property>

        <Property label="Level">
          <LevelPicker task={t} onSave={(body) => patch(body, "Level saved")} />
        </Property>

        {/* An epic is a body of work, not a kind of change: its type is left
            to the board's default and not offered. */}
        {t.level !== "epic" && (
          <Property label="Type">
            <PickerButton
              current={
                <>
                  <TypeIcon type={t.type} /> {typeOf(config, t.type).name}
                </>
              }
              items={config.types.map((type) => ({
                label: (
                  <span className="flex items-center gap-2">
                    <TypeIcon type={type.id} /> {type.name}
                  </span>
                ),
                selected: type.id === t.type,
                onSelect: () => patch({ type: type.id }),
              }))}
            />
          </Property>
        )}

        {/* An epic has no parent; a task's is an epic, a sub-task's a task. */}
        {parentLevelOf(t.level) && (
          <Property label={t.level === "task" ? "Epic" : "Parent"}>
            <ParentPicker task={t} onSave={(parentTaskId) => patch({ parentTaskId }, "Parent saved")} />
          </Property>
        )}

        <Property label="Sprint">
          <SprintPicker task={t} onSave={(sprintId) => patch({ sprintId }, "Sprint saved")} />
        </Property>

        {/* The project's custom fields, one Property each. Values PATCH as a
            single-key object: the server merges nothing — it validates and
            stores what it is sent — so each control sends the full map with
            its own key changed. */}
        {config.fields.map((field) => (
          <Property key={field.id} label={field.name}>
            <CustomFieldInput
              field={field}
              value={t.customFields?.[field.id]}
              onSave={(value) =>
                patch({ customFields: { ...(t.customFields ?? {}), [field.id]: value } })
              }
            />
          </Property>
        ))}

        <Property label="Labels">
          <LabelEditor labels={t.labels} onSave={(labels) => patch({ labels })} />
        </Property>

        <Property label="Start">
          <DateInput
            value={t.startDate}
            ariaLabel="Start date"
            onSave={(startDate) => patch({ startDate })}
          />
        </Property>

        <Property label="Due">
          <DateInput
            value={t.dueDate}
            ariaLabel="Due date"
            overdue={isOverdue(t.dueDate, t.status)}
            onSave={(dueDate) => patch({ dueDate })}
          />
        </Property>

        <Property label="Estimate">
          <EstimateInput
            value={t.estimateMinutes}
            onSave={(estimateMinutes) => patch({ estimateMinutes })}
            onInvalid={() => toast("Could not read that estimate — try 3h, 2h 30m or 1d", "error")}
          />
        </Property>

        <Property label="Project">
          <MoveToProject task={t} onChange={task.reload} />
        </Property>

        <TimePanel
          parent={{ type: "task", id: taskId }}
          item={t}
          level={t.level}
          estimate={t.estimateMinutes}
          childEstimate={t.childEstimateMinutes ?? 0}
        />

        <div className="mt-2 flex flex-col gap-1 border-t border-border pt-3 text-xs text-faint">
          {t.statusChangedAt && (
            <p>
              Moved to {statusOf(config, t.status).name} by{" "}
              {t.statusChangedBy ? (roster.get(t.statusChangedBy) ?? "someone") : "the agent"}{" "}
              <RelativeTime value={t.statusChangedAt} />
            </p>
          )}
          <p>
            Created {formatDate(t.createdAt)} by {roster.get(t.createdBy) ?? "the agent"}
          </p>
          <ArchiveToggle task={t} onChange={task.reload} />
        </div>
      </aside>

      {creatingChild && (
        <TaskCreateModal
          projectId={t.projectId}
          employees={assignable}
          config={config}
          parent={{ id: t.id, key: t.key, title: t.title, level: t.level }}
          onClose={() => setCreatingChild(false)}
          onCreated={task.reload}
        />
      )}
    </div>
    </BoardConfigProvider>
  )
}

// ---------- layout pieces ----------

function Section({
  title,
  aside,
  children,
}: {
  title: string
  aside?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  )
}

function Property({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="w-20 shrink-0 text-xs text-faint">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

function PickerButton({ current, items }: { current: React.ReactNode; items: MenuItem[] }) {
  return (
    <Dropdown
      items={items}
      trigger={({ toggle }) => (
        <button
          type="button"
          onClick={toggle}
          className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-sm text-foreground transition-colors hover:bg-surface-muted"
        >
          <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate">{current}</span>
          <ChevronDown className="size-3 shrink-0 text-faint" />
        </button>
      )}
    />
  )
}

/** Rendered markdown until clicked, a textarea after. Editing the source rather
 *  than the rendering is deliberate — the agent writes markdown, and a rich
 *  editor would rewrite its formatting on every save.
 *
 *  `resolveHref` paints the description's `attachment:<id>` images — resolved
 *  at paint time against the task's own attachment list, the same way a
 *  comment's are. */
function EditableMarkdown({
  value,
  onSave,
  ariaLabel,
  placeholder,
  resolveHref,
  onUpload,
}: {
  value: string
  onSave: (next: string) => void
  ariaLabel: string
  placeholder: string
  resolveHref?: (url: string) => string | null
  onUpload?: (file: File) => Promise<string>
}) {
  const [editing, setEditing] = useState(false)
  const [lossy, setLossy] = useState<{ next: string; diff: ReturnType<typeof differences> } | null>(
    null,
  )
  const field = useRef<MarkdownFieldHandle | null>(null)

  const commit = (next: string) => {
    onSave(next.trim())
    setEditing(false)
  }

  // The block editor round-trips through remark, which is not byte-identical
  // to every way markdown can be written. The agent writes these descriptions,
  // so an unrelated edit reformatting one is a diff nobody made — asked about
  // rather than done, and only when the author changed nothing themselves.
  const save = () => {
    const next = (field.current?.getValue() ?? value).trim()
    const diff = differences(value.trim(), next)
    if (next !== value.trim() && diff.length > 0 && !touched.current) {
      setLossy({ next, diff })
      return
    }
    commit(next)
  }

  const touched = useRef(false)

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          touched.current = false
          setEditing(true)
        }}
        aria-label={`${ariaLabel} — click to edit`}
        className="-mx-2 rounded-md px-2 py-1 text-left transition-colors hover:bg-surface-muted"
      >
        {value.trim() ? (
          <Markdown resolveHref={resolveHref}>{value}</Markdown>
        ) : (
          <span className="text-sm text-faint">{placeholder}</span>
        )}
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <MarkdownField
        initialValue={value}
        handle={field}
        onChange={() => {
          touched.current = true
        }}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
        startEditing
        onUpload={onUpload}
        proxyDomURL={(url) => resolveHref?.(url) ?? url}
        onKeyDown={(event) => {
          if (event.key === "Escape") setEditing(false)
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) save()
        }}
      />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save}>
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          Cancel
        </Button>
        <span className="text-xs text-faint">⌘↵ saves, Esc cancels.</span>
      </div>

      {lossy && (
        <Modal title="Saving would reformat this" onClose={() => setLossy(null)}>
          <p className="text-sm text-muted">
            The block editor cannot represent part of this text exactly, so saving would rewrite
            these lines. Nothing has been written yet.
          </p>
          <ul className="flex max-h-56 flex-col gap-2 overflow-y-auto rounded-md border border-border p-3">
            {lossy.diff.map((d) => (
              <li key={d.line} className="flex flex-col gap-0.5 font-mono text-[11px]">
                <span className="text-faint">line {d.line}</span>
                <span className="text-danger">− {d.before || "(empty)"}</span>
                <span className="text-success">+ {d.after || "(empty)"}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={() => setLossy(null)}>
              Keep the original
            </Button>
            <Button
              onClick={() => {
                const next = lossy.next
                setLossy(null)
                commit(next)
              }}
            >
              Save anyway
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

function LabelEditor({ labels, onSave }: { labels: string[]; onSave: (next: string[]) => void }) {
  const [adding, setAdding] = useState(false)

  return (
    <div className="flex flex-wrap items-center gap-1">
      {labels.map((label) => (
        <span key={label} className="group/label">
          <Badge>
            {label}
            <button
              type="button"
              aria-label={`Remove label ${label}`}
              onClick={() => onSave(labels.filter((l) => l !== label))}
              className="text-faint hover:text-danger"
            >
              ×
            </button>
          </Badge>
        </span>
      ))}

      {adding ? (
        <input
          autoFocus
          aria-label="New label"
          placeholder="label"
          onBlur={() => setAdding(false)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setAdding(false)
            if (event.key === "Enter") {
              const value = event.currentTarget.value.trim()
              // Duplicates are dropped server-side too; catching it here avoids
              // a pointless round trip and a chip that appears to do nothing.
              if (value && !labels.includes(value)) onSave([...labels, value])
              setAdding(false)
            }
          }}
          className="w-24 rounded border border-interactive bg-surface px-1.5 py-0.5 text-xs outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="rounded px-1.5 py-0.5 text-xs text-faint hover:bg-surface-muted hover:text-foreground"
        >
          + Add
        </button>
      )}
    </div>
  )
}

function DateInput({
  value,
  onSave,
  ariaLabel,
  overdue,
}: {
  value: string | null
  onSave: (next: string | null) => void
  ariaLabel: string
  overdue?: boolean
}) {
  return (
    <span className="flex items-center gap-1">
      <input
        type="date"
        aria-label={ariaLabel}
        value={toDateInput(value)}
        onChange={(event) => onSave(fromDateInput(event.target.value))}
        className={`w-full rounded-md bg-transparent px-1 py-1 text-sm outline-none transition-colors hover:bg-surface-muted focus:bg-surface-muted ${
          overdue ? "text-danger" : value ? "text-foreground" : "text-faint"
        }`}
      />
    </span>
  )
}

/** The estimate, typed the way time is logged ("3h", "2h 30m", "1d"). Saves on
 *  blur or Enter; an empty box clears it, and unreadable text is refused rather
 *  than saved as a guess. Keyed on the stored value so a reload resets it. */
function EstimateInput({
  value,
  onSave,
  onInvalid,
}: {
  value: number | null
  onSave: (next: number | null) => void
  onInvalid: () => void
}) {
  const [draft, setDraft] = useState(value ? formatDuration(value) : "")
  const [shown, setShown] = useState(value)
  if (shown !== value) {
    setShown(value)
    setDraft(value ? formatDuration(value) : "")
  }

  const commit = () => {
    const text = draft.trim()
    if (!text) {
      if (value !== null) onSave(null)
      return
    }
    const minutes = parseDuration(text)
    if (minutes === null) {
      onInvalid()
      setDraft(value ? formatDuration(value) : "")
      return
    }
    if (minutes !== value) onSave(minutes)
    else setDraft(formatDuration(minutes))
  }

  return (
    <span className="flex items-center gap-1">
      <input
        type="text"
        aria-label="Estimate"
        placeholder="Not estimated"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur()
          if (event.key === "Escape") {
            setDraft(value ? formatDuration(value) : "")
            event.currentTarget.blur()
          }
        }}
        className={`w-full rounded-md bg-transparent px-1 py-1 text-sm outline-none transition-colors placeholder:text-faint hover:bg-surface-muted focus:bg-surface-muted ${
          value ? "text-foreground" : "text-faint"
        }`}
      />
      {value !== null && (
        <button
          type="button"
          aria-label="Clear estimate"
          title="Clear estimate"
          onClick={() => onSave(null)}
          className="shrink-0 px-1 text-xs text-faint hover:text-foreground"
        >
          ×
        </button>
      )}
    </span>
  )
}

function ChildRow({
  task,
  config,
}: {
  task: Task
  config: Project["config"]
}) {
  const name = task.assigneeName
  return (
    <li
      className={`flex items-center gap-2 border-b border-border px-3 py-2 last:border-0 ${
        task.archivedAt ? "opacity-60" : ""
      }`}
    >
      <StatusIcon status={task.status} />
      <LevelIcon level={task.level} />
      <Link to={`/tasks/${task.id}`} className="font-mono text-[11px] text-faint hover:text-muted hover:underline">
        {task.key}
      </Link>
      <Link to={`/tasks/${task.id}`} className="min-w-0 flex-1 truncate text-sm text-foreground hover:underline">
        {task.title}
      </Link>
      <span className="hidden shrink-0 text-xs text-faint sm:inline">{statusOf(config, task.status).name}</span>
      {task.archivedAt && <Badge tone="warning">Archived</Badge>}
      <Avatar name={name} id={task.assigneeId} size="xs" />
    </li>
  )
}

/** Where an item sits. Offers only the levels the server would accept given
 *  its parent and its children — an epic with tasks cannot become a task, a
 *  task with sub-tasks cannot become a sub-task. Becoming an epic clears the
 *  parent in the same save, because an epic has none. */
function LevelPicker({
  task,
  onSave,
}: {
  task: Task
  onSave: (body: { level: TaskLevel; parentTaskId?: null }) => void
}) {
  const choices = allowedLevels({
    level: task.level,
    parentLevel: task.parent?.level ?? null,
    childLevels: (task.children ?? []).map((child) => child.level),
  })
  return (
    <PickerButton
      current={
        <>
          <LevelIcon level={task.level} /> {LEVEL_LABELS[task.level]}
        </>
      }
      items={choices.map((level) => ({
        label: (
          <span className="flex items-center gap-2">
            <LevelIcon level={level} /> {LEVEL_LABELS[level]}
          </span>
        ),
        selected: level === task.level,
        onSelect: () => {
          if (level === task.level) return
          onSave(level === "epic" && task.parentTaskId ? { level, parentTaskId: null } : { level })
        },
      }))}
    />
  )
}

/** The way, from the browser, to put a task under an epic or a sub-task under
 *  a task. Offers only items of the level this one's parent has to be, in the
 *  same project and not archived. A task may also stand alone; a sub-task
 *  always has a parent, so it gets no "No parent". */
function ParentPicker({ task, onSave }: { task: Task; onSave: (parentTaskId: string | null) => void }) {
  const wanted = parentLevelOf(task.level)
  const candidates = useLoad(
    () =>
      wanted
        ? api.get<{ tasks: Task[] }>(`/projects/${task.projectId}/tasks?level=${wanted}`)
        : Promise.resolve({ tasks: [] as Task[] }),
    [task.projectId, wanted],
  )
  const options = (candidates.data?.tasks ?? []).filter((c) => c.id !== task.id && !c.archivedAt)

  return (
    <PickerButton
      current={
        task.parent ? (
          <span className="truncate">
            <span className="font-mono text-xs text-faint">{task.parent.key}</span> {task.parent.title}
          </span>
        ) : (
          <span className="text-faint">{task.level === "task" ? "No epic" : "No parent"}</span>
        )
      }
      items={[
        ...(task.level === "task"
          ? [{ label: "No epic", selected: !task.parentTaskId, onSelect: () => onSave(null) }]
          : []),
        ...options.map((option) => ({
          label: (
            <span className="flex min-w-0 items-center gap-2">
              <span className="font-mono text-[11px] text-faint">{option.key}</span>
              <span className="truncate">{option.title}</span>
            </span>
          ),
          selected: option.id === task.parentTaskId,
          onSelect: () => onSave(option.id),
        })),
      ]}
    />
  )
}

/** Planned and active sprints only: a closed sprint is history, and the server
 *  refuses to put work into one. */
function SprintPicker({ task, onSave }: { task: Task; onSave: (sprintId: string | null) => void }) {
  const sprints = useLoad(
    () => api.get<{ sprints: Sprint[] }>(`/projects/${task.projectId}/sprints`),
    [task.projectId],
  )
  const all = sprints.data?.sprints ?? []
  const current = all.find((sprint) => sprint.id === task.sprintId)
  const open = all.filter((sprint) => sprint.status !== "closed")

  return (
    <PickerButton
      current={
        task.sprintId ? (
          <span className="flex min-w-0 items-center gap-1.5 truncate">
            {current?.name ?? task.sprintName ?? "Sprint"}
            {current?.status === "active" && <Badge tone="accent">Current</Badge>}
          </span>
        ) : (
          <span className="text-faint">No sprint</span>
        )
      }
      items={[
        { label: "No sprint", selected: !task.sprintId, onSelect: () => onSave(null) },
        ...open.map((sprint) => ({
          label: (
            <span className="flex items-center gap-2">
              {sprint.name}
              {sprint.status === "active" && <Badge tone="accent">Current</Badge>}
            </span>
          ),
          selected: sprint.id === task.sprintId,
          onSelect: () => onSave(sprint.id),
        })),
      ]}
    />
  )
}

function MoveToProject({ task, onChange }: { task: Task; onChange: () => void }) {
  const toast = useToast()
  const projects = useLoad(() => api.get<{ projects: Project[] }>("/projects"))
  const here = projects.data?.projects.find((p) => p.id === task.projectId)

  return (
    <PickerButton
      current={<span className="truncate">{here?.name ?? "This project"}</span>}
      items={(projects.data?.projects ?? [])
        .filter((p) => p.id !== task.projectId)
        .map((project) => ({
          label: `Move to ${project.name}`,
          onSelect: async () => {
            // The task comes back with a different key: the unique index is per
            // project and the target has its own counter.
            const moved = await api.post<{ task: Task }>(`/projects/tasks/${task.id}/move`, {
              projectId: project.id,
            })
            toast(`Moved — now ${moved.task.key}`)
            onChange()
          },
        }))}
    />
  )
}

function ArchiveToggle({ task, onChange }: { task: Task; onChange: () => void }) {
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  const act = async (action: "archive" | "restore") => {
    setBusy(true)
    try {
      await api.post(`/projects/tasks/${task.id}/${action}`, {})
      toast(action === "archive" ? "Task archived" : "Task restored")
      onChange()
    } finally {
      setBusy(false)
    }
  }

  const open = task.openChildren ?? 0
  return (
    <span className="flex flex-col gap-0.5">
      <button
        type="button"
        disabled={busy}
        onClick={() => act(task.archivedAt ? "restore" : "archive")}
        className="self-start underline underline-offset-2 hover:text-foreground"
      >
        {task.archivedAt ? `Restore this ${LEVEL_LABELS[task.level].toLowerCase()}` : `Archive this ${LEVEL_LABELS[task.level].toLowerCase()}`}
      </button>
      {/* Archiving does not cascade — said before the click, not after. */}
      {!task.archivedAt && open > 0 && (
        <span className="text-warning">
          {open} {open === 1 ? (task.level === "epic" ? "task" : "sub-task") : CHILDREN_LABEL[task.level]}{" "}
          {open === 1 ? "stays" : "stay"} open
        </span>
      )}
    </span>
  )
}

// ---------- time ----------

function TimePanel({
  parent,
  item,
  level = "task",
  estimate = null,
  childEstimate = 0,
}: {
  parent: { type: "task"; id: string }
  /** What the edit dialog names the entry's task by. */
  item: Pick<Task, "key" | "title">
  /** What the children are called: an epic's are tasks, a task's sub-tasks. */
  level?: TaskLevel
  /** The item's own estimate, and its subtree's summed. */
  estimate?: number | null
  childEstimate?: number
}) {
  const { employee, company } = useSession()
  const toast = useToast()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<EditableTimeEntry | null>(null)
  const path = `/projects/tasks/${parent.id}/time`
  const logged = useLoad(() => api.get<TimeLog>(path), [path])

  // The server's rule, mirrored so the controls only show where they work:
  // your own entries, or anyone's for an admin.
  const canManage = (entry: TimeEntry) =>
    entry.employeeId === employee.id || employee.role === "admin" || employee.role === "platform_admin"

  async function remove(entry: TimeEntry) {
    if (!window.confirm(`Delete ${formatDuration(entry.minutes)} logged by ${entry.employeeName}?`)) return
    try {
      await api.delete(`/projects/time/${entry.id}`)
      toast(`Deleted ${formatDuration(entry.minutes)}`)
    } catch (err) {
      // Already gone — deleted from the timesheet or the Time Report — is the
      // outcome asked for; the reload below shows it.
      if (!(err instanceof ApiError && err.status === 404)) {
        toast(err instanceof Error ? err.message : "Could not delete that entry", "error")
      }
    }
    logged.reload()
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    const minutes = parseDuration(String(data.get("duration") ?? ""))
    if (!minutes) {
      setError("Try 1.5, 90m or 2h 30m.")
      return
    }

    setBusy(true)
    setError(null)
    try {
      await api.post(path, {
        minutes,
        spentOn: fromDateInput(String(data.get("spentOn") ?? "")) ?? undefined,
        note: String(data.get("note") ?? "").trim() || undefined,
      })
      form.reset()
      toast(`Logged ${formatDuration(minutes)}`)
      logged.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not log that")
    } finally {
      setBusy(false)
    }
  }

  const entries = logged.data?.entries ?? []
  const own = logged.data?.totalMinutes ?? 0
  const childMinutes = logged.data?.childMinutes ?? 0

  return (
    <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
      <div className="flex items-center gap-2">
        <Clock className="size-3.5 text-faint" />
        <span className="text-xs text-faint">Time</span>
        <span className="ml-auto font-mono text-sm text-foreground tabular-nums">
          {formatDuration(own)}
        </span>
      </div>

      {!!estimate && logged.data && (
        <p className={`text-xs ${own > estimate ? "font-medium text-danger" : "text-faint"}`}>
          {describeSpentOfEstimate(own, estimate)}
        </p>
      )}

      {/* A parent's own figure stays its own; the children's time is shown
          beside it rather than folded in, so neither number hides the other. */}
      {!!logged.data && (childMinutes > 0 || childEstimate > 0) && (
        <details className="text-xs text-faint">
          <summary className="flex cursor-pointer items-center gap-2">
            <span>Including {CHILDREN_LABEL[level]}</span>
            <span className="ml-auto font-mono text-muted tabular-nums">
              {formatDuration(own + childMinutes)}
            </span>
          </summary>
          {childEstimate > 0 && (
            <p className={`mt-1 pl-3 ${childMinutes > childEstimate ? "font-medium text-danger" : ""}`}>
              Children: {describeSpentOfEstimate(childMinutes, childEstimate).replace(/^Logged/, "logged")}
            </p>
          )}
          <ul className="mt-1 flex flex-col gap-0.5 pl-3">
            {(logged.data.children ?? [])
              .filter((child) => child.minutes > 0 || !!child.estimateMinutes)
              .map((child) => (
                <li key={child.taskId} className="flex items-baseline gap-2">
                  <Link to={`/tasks/${child.taskId}`} className="font-mono hover:text-muted hover:underline">
                    {child.key}
                  </Link>
                  <span className="min-w-0 flex-1 truncate" title={child.title}>
                    {child.title}
                  </span>
                  <span
                    className={`shrink-0 font-mono tabular-nums ${
                      child.estimateMinutes && child.minutes > child.estimateMinutes ? "text-danger" : ""
                    }`}
                    title={child.estimateMinutes ? describeSpentOfEstimate(child.minutes, child.estimateMinutes) : undefined}
                  >
                    {child.estimateMinutes
                      ? formatSpentOfEstimate(child.minutes, child.estimateMinutes)
                      : formatDuration(child.minutes)}
                  </span>
                </li>
              ))}
          </ul>
        </details>
      )}

      {entries.length > 0 && (
        <ul className="flex flex-col gap-1">
          {entries.map((entry) => (
            <li key={entry.id} className="group flex items-baseline gap-2 text-xs">
              <span className="w-12 shrink-0 font-mono text-muted tabular-nums">
                {formatDuration(entry.minutes)}
              </span>
              <span className="min-w-0 flex-1 truncate text-faint" title={entry.note ?? undefined}>
                {entry.employeeName}
                {entry.note ? ` — ${entry.note}` : ""}
              </span>
              <span className="shrink-0 text-faint">{entry.spentOn.slice(5, 10)}</span>
              {canManage(entry) && (
                <>
                  <button
                    type="button"
                    aria-label="Edit entry"
                    title="Edit"
                    onClick={() => setEditing(editableEntry(entry, { id: parent.id, ...item }, company))}
                    className="shrink-0 self-center text-faint opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground focus-visible:opacity-100"
                  >
                    <Pencil className="size-3" />
                  </button>
                  <button
                    type="button"
                    aria-label="Delete entry"
                    title="Delete"
                    onClick={() => void remove(entry)}
                    className="shrink-0 text-faint opacity-0 transition-opacity group-hover:opacity-100 hover:text-danger focus-visible:opacity-100"
                  >
                    ×
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex flex-col gap-1.5">
        <div className="flex gap-1.5">
          <input
            name="duration"
            aria-label="Duration"
            placeholder="1.5"
            className="w-16 rounded-md border border-border bg-surface px-2 py-1 text-xs outline-none placeholder:text-faint focus:border-interactive"
          />
          <input
            name="spentOn"
            type="date"
            aria-label="Date the work happened"
            defaultValue={new Date().toISOString().slice(0, 10)}
            className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2 py-1 text-xs outline-none focus:border-interactive"
          />
        </div>
        <input
          name="note"
          aria-label="Note"
          placeholder="What you did (optional)"
          className="rounded-md border border-border bg-surface px-2 py-1 text-xs outline-none placeholder:text-faint focus:border-interactive"
        />
        {error && <p className="text-xs text-danger">{error}</p>}
        <Button type="submit" size="sm" variant="ghost" disabled={busy}>
          {busy ? "Logging…" : "Log time"}
        </Button>
      </form>

      {/* The timesheet's own Edit spent time dialog, so an entry corrects the
          same way from either page: duration, day and note, the task fixed. */}
      {editing && (
        <EntryDialog
          key={editing.id}
          entry={editing}
          date={editing.date}
          // The page already reads as this company; the dialog's company line
          // is only for a sheet that spans several.
          companies={[]}
          defaultCompanyId={company.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            logged.reload()
          }}
        />
      )}
    </div>
  )
}

// ---------- comments ----------

function CommentThread({ taskId, sort }: { taskId: string; sort: CommentSort }) {
  const { employee } = useSession()
  const path = `/projects/tasks/${taskId}/comments`
  const { data, loading, reload } = useLoad(() => api.get<{ comments: Comment[] }>(path), [path])
  const [error, setError] = useState<unknown>(null)

  const canManage = (comment: Comment) =>
    comment.authorId === employee.id || employee.role === "admin" || employee.role === "platform_admin"

  async function handleSubmit(
    body: string,
    removeAttachmentIds: string[],
    attachmentIds: string[],
  ) {
    setError(null)
    const formData = new FormData()
    formData.append("body", body)
    if (removeAttachmentIds.length > 0) {
      formData.append("removeAttachmentIds", JSON.stringify(removeAttachmentIds))
    }
    if (attachmentIds.length > 0) {
      formData.append("attachmentIds", JSON.stringify(attachmentIds))
    }
    await api.postMultipart(path, formData)
    reload()
  }

  // With newest first the composer leads, so a new comment lands right under
  // where it was written instead of a whole thread away. It moves by CSS order
  // rather than by position in the tree, so flipping the sort does not remount
  // the editor and throw away a half-written comment.
  const composer = (
    <div className={`flex gap-2.5 ${sort === "newest" ? "order-first" : ""}`}>
      <Avatar name={employee.name} id={employee.id} />
      <div className="min-w-0 flex-1">
        <CommentEditor
          onSubmit={handleSubmit}
          placeholder="Correct the plan, or note what you found."
        />
        <ErrorNote error={error} />
      </div>
    </div>
  )

  return (
    <div className="flex flex-col gap-3">
      {loading ? (
        <Skeleton className="h-16" />
      ) : (
        sortComments(data?.comments ?? [], sort).map((comment) => (
          <div key={comment.id} className="group flex gap-2.5">
            <Avatar name={comment.authorName} id={comment.authorId} />
            <div className="min-w-0 flex-1">
              <CommentDisplay
                comment={comment}
                canEdit={canManage(comment)}
                canDelete={canManage(comment)}
                onUpdate={reload}
                onDelete={reload}
              />
            </div>
          </div>
        ))
      )}
      {composer}
    </div>
  )
}

/** One custom-field control, typed by its definition. Values save on commit
 *  (blur/enter for text and number, change for date and select); null clears —
 *  the same convention the task's own nullable columns follow. */
function CustomFieldInput({
  field,
  value,
  onSave,
}: {
  field: { id: string; name: string; type: "text" | "number" | "date" | "select"; options?: string[] }
  value: string | number | undefined
  onSave: (value: string | number | null) => void
}) {
  if (field.type === "select") {
    return (
      <PickerButton
        current={<span className={value ? "" : "text-faint"}>{value ?? "None"}</span>}
        items={[
          ...(field.options ?? []).map((option) => ({
            label: <span>{option}</span>,
            selected: option === value,
            onSelect: () => onSave(option),
          })),
          { label: <span className="text-faint">Clear</span>, selected: false, onSelect: () => onSave(null) },
        ]}
      />
    )
  }

  if (field.type === "date") {
    return (
      <DateInput
        value={typeof value === "string" ? value : null}
        ariaLabel={field.name}
        onSave={(iso) => onSave(iso)}
      />
    )
  }

  return (
    <input
      type={field.type === "number" ? "number" : "text"}
      defaultValue={value ?? ""}
      aria-label={field.name}
      placeholder="—"
      onBlur={(e) => {
        const raw = e.target.value.trim()
        if (raw === String(value ?? "")) return
        if (raw === "") return onSave(null)
        onSave(field.type === "number" ? Number(raw) : raw)
      }}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-sm text-foreground outline-none hover:border-border focus:border-interactive"
    />
  )
}
